import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/shadcn/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/shadcn/select";
import { ExportToExcelButton } from "@/components/general/common/ExportToExcelButton";
import { exportPlayerAnalysisToExcel } from "@/api/excelExport";
import { useSelectedTeam } from "@/context/teamContext";
import { playerSummaryApi } from "@/api/playerSummary";
import { GenerateSummaryButton } from "@/components/general/common/GenerateSummaryButton";
import { SummaryDialog } from "@/components/general/common/SummaryDialog";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { followedPlayerIdsQueryOptions } from "@/api/followedPlayers";
import { FollowPlayerButton } from "@/components/general/Scouting/FollowPlayerButton";

import type { PlayerTournament } from "@/api/tournaments";

export function PlayerHeader({
    playerId,
    playerName,
    playerTeamId,
    selectedTournament,
    onTournamentChange,
    selectedTournamentId,
    playerTournaments,
}: {
    playerId?: number;
    playerName?: string;
    playerTeamId?: number;
    selectedTournament?: string;
    onTournamentChange?: (tournament: string) => void;
    selectedTournamentId?: number;
    playerTournaments?: PlayerTournament[];
}) {
    const { teamId } = useSelectedTeam();
    const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
    const { data: followedPlayerIds } = useQuery(followedPlayerIdsQueryOptions());
    const isFollowed = playerId !== undefined && (followedPlayerIds?.includes(playerId) ?? false);
    const [playerSummaryId, setPlayerSummaryId] = useState<number | null>(null);
    const [playerSummary, setPlayerSummary] = useState<string>("");

    const currentTournament = playerTournaments?.find((t) => t.id === selectedTournamentId);
    const displayTournamentName = currentTournament?.name || selectedTournament;

    const handleTournamentSelect = (tournamentName: string) => {
        onTournamentChange?.(tournamentName);
    };

    const handleSummaryGenerated = (summaryId: number, summary: string) => {
        setPlayerSummaryId(summaryId);
        setPlayerSummary(summary);
        setIsSummaryDialogOpen(true);
    };

    const handleSummaryUpdated = (newSummary: string, newSummaryId: number) => {
        setPlayerSummary(newSummary);
        setPlayerSummaryId(newSummaryId);
    };

    return (
        <TooltipProvider>
            <div
                id="onboarding-player-header"
                className="flex flex-wrap items-center gap-3 mb-3 pb-3 border-b border-outline-variant/30"
            >
                <div className="flex flex-col gap-2 w-full md:flex-row md:items-center md:flex-wrap md:w-auto">
                    <Select
                        value={displayTournamentName || ""}
                        onValueChange={handleTournamentSelect}
                        disabled={!playerTournaments || playerTournaments.length === 0}
                    >
                        <SelectTrigger className="w-full md:w-fit">
                            <SelectValue placeholder="Select tournament" />
                        </SelectTrigger>
                        <SelectContent>
                            {playerTournaments && playerTournaments.length > 0 ? (
                                playerTournaments.map((tournament) =>
                                    !tournament.doesPlayerParticipate ? (
                                        <Tooltip key={tournament.id} delayDuration={0}>
                                            <TooltipTrigger asChild>
                                                <div
                                                    className="flex items-center px-2 py-1.5 text-sm opacity-50 cursor-not-allowed text-on-surface"
                                                    onClick={(e) => e.preventDefault()}
                                                >
                                                    <div className="flex items-baseline gap-2">
                                                        <span>{tournament.name}</span>
                                                        <span className="text-sm text-on-surface-variant">
                                                            {tournament.id}
                                                        </span>
                                                    </div>
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="left">
                                                <p>Player did not participate in this tournament</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <SelectItem key={tournament.id} value={tournament.name}>
                                            <div className="flex items-baseline gap-2">
                                                <span>{tournament.name}</span>
                                                <span className="text-sm text-on-surface-variant">{tournament.id}</span>
                                            </div>
                                        </SelectItem>
                                    )
                                )
                            ) : (
                                <div className="px-3 py-2 text-on-surface-variant text-sm">
                                    No tournaments available
                                </div>
                            )}
                        </SelectContent>
                    </Select>
                    <div className="flex flex-col items-center gap-2 md:contents">
                        {playerId && playerTeamId !== 527810 && (
                            <FollowPlayerButton playerId={playerId} isFollowed={isFollowed} />
                        )}
                        {playerId && selectedTournamentId && (
                            <GenerateSummaryButton
                                mutationFn={() =>
                                    playerSummaryApi.generateSummary(playerId, selectedTournamentId, teamId)
                                }
                                onSummaryGenerated={handleSummaryGenerated}
                            />
                        )}
                        {playerId && (
                            <ExportToExcelButton
                                mutationFn={() => exportPlayerAnalysisToExcel(playerId, selectedTournamentId, teamId)}
                                label="Export Analysis to Excel"
                                successMessage={`Player analysis Excel file ${selectedTournament ? `for ${playerName} in ${selectedTournament}` : `for ${playerName}`} downloaded successfully!`}
                            />
                        )}
                    </div>
                </div>
            </div>
            {playerSummaryId && (
                <SummaryDialog
                    isOpen={isSummaryDialogOpen}
                    onClose={() => setIsSummaryDialogOpen(false)}
                    summaryId={playerSummaryId}
                    summary={playerSummary}
                    entityType="player_summary"
                    title="AI Player Performance Summary"
                    subtitle={
                        <>
                            <span className="font-medium">{playerName || ""}</span>
                            <span className="mx-2">&bull;</span>
                            <span>{selectedTournament || ""}</span>
                        </>
                    }
                    regenerateFn={() =>
                        playerSummaryApi.regenerateSummary(playerId || 0, selectedTournamentId || 0, teamId)
                    }
                    onSummaryUpdated={handleSummaryUpdated}
                />
            )}
        </TooltipProvider>
    );
}
